'use client'

import Link from 'next/link'
import { MapPin } from 'lucide-react'
import React from 'react'

import { containerClassName } from '@/components/Container'
import { PageHero } from '@/components/Hero/PageHero'
import { HeroMediaOverlay } from '@/components/Hero/HeroMediaOverlay'
import {
  PAGE_HERO_FULL_VIEWPORT_CLASS_NAME,
  pageHeroFullViewportBottomContentCompactClassName,
  pageHeroVideoControlClassName,
} from '@/components/Hero/pageHeroLayout'
import {
  HeroVideoLayer,
  HeroVideoPlaybackButton,
  useHeroVideoBackground,
} from '@/components/Hero/HeroVideoBackground'
import { Button } from '@/components/ui/button'
import { localizePath } from '@/lib/i18n/config'
import { useLocale } from '@/lib/i18n/useLocale'
import type { LocationPage } from '@/lib/mock/locationPages'
import { useMessages } from '@/lib/i18n/useMessages'
import { getInspectionCalendarHref } from '@/lib/pages/inspectionCalendarLink'
import { cn } from '@/utilities/ui'

type LocationHeroProps = {
  location: LocationPage
  showPromotionsLink?: boolean
}

export const LocationHero: React.FC<LocationHeroProps> = ({
  location,
}) => {
  const locale = useLocale()
  const messages = useMessages()

  const heroVideo = useHeroVideoBackground({
    desktopSrc: location.heroVideoDesktop,
    mobileSrc: location.heroVideoMobile,
    poster: location.heroImage,
    posterAlt: location.heroImageAlt,
  })

  return (
    <PageHero
      aria-label={`${location.name} overview`}
      as="section"
      background={
        <>
          <HeroVideoLayer video={heroVideo} />
          <HeroMediaOverlay tone="location" />
        </>
      }
      className={`${PAGE_HERO_FULL_VIEWPORT_CLASS_NAME} !overflow-visible`}
      contentClassName={pageHeroFullViewportBottomContentCompactClassName}
      contentWrapperClassName={cn(
        containerClassName,
        'relative flex w-full flex-1 flex-col items-start py-0 lg:py-0',
      )}
      gradientClassName={false}
      heightMode="viewport"
      pullUnderHeader={false}
    >
      <HeroVideoPlaybackButton className={pageHeroVideoControlClassName} video={heroVideo} />

      <p className="flex items-center gap-1 font-outfit text-base font-semibold text-white">
        <MapPin aria-hidden className="size-4 shrink-0 text-turquoise" />
        {location.address}
      </p>

      <h1 className="-mt-2 max-w-full break-words font-display text-[3rem] font-semibold leading-none tracking-[-0.04em] text-white drop-shadow-[0_16px_32px_-8px_rgba(12,12,13,0.4)] md:max-w-[41rem] md:text-[4rem]">
        {location.name}
      </h1>

      <div className="flex flex-wrap gap-3">
        <Button asChild className="h-11 px-4">
          <Link href={getInspectionCalendarHref()} rel="noopener noreferrer" target="_blank">
            {messages.cityPage.primaryCta}
            <span className="sr-only"> {messages.common.opensInNewTab}</span>
          </Link>
        </Button>

        <Button asChild className="h-11 px-4">
          <Link href={localizePath('/apartments', locale)}>
            {messages.cityPage.secondaryCta}
          </Link>
        </Button>
      </div>
    </PageHero>
  )
}
